import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, Globe, Menu, Book, Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { usePendingJob } from '../contexts/PendingJobContext';
import TenantSelector from './TenantSelector';
import ProjectSelector from './ProjectSelector';
import NotificationBell from './common/NotificationBell';

interface HeaderProps {
  onMenuClick: () => void;
}

const Header = ({ onMenuClick }: HeaderProps) => {
  const { logout, user } = useAuth();
  const { t, i18n } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const { loadJob } = usePendingJob();
  const navigate = useNavigate();
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);

  const languages = [
    { code: 'en', name: t('english'), flag: '🇺🇸' },
    { code: 'es', name: t('spanish'), flag: '🇪🇸' },
    { code: 'fr', name: t('french'), flag: '🇫🇷' },
    { code: 'it', name: t('italian'), flag: '🇮🇹' },
    { code: 'ar', name: t('arabic'), flag: '🇸🇦' },
    { code: 'zh', name: t('chinese'), flag: '🇨🇳' },
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const changeLanguage = useCallback((langCode: string) => {
    i18n.changeLanguage(langCode);
    setShowLanguageMenu(false);
  }, [i18n]);

  const handleJobCompleted = useCallback(async (jobId: string, gameName: string) => {
    await loadJob(jobId, gameName);
    navigate('/instant-win');
  }, [loadJob, navigate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.language-selector')) {
        setShowLanguageMenu(false);
      }
    };

    if (showLanguageMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLanguageMenu]);

  return (
    <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 transition-colors duration-200">
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center space-x-4 flex-1">
          {/* Mobile menu button */}
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>

        <div className="flex items-center space-x-2 sm:space-x-4">
          {/* Tenant Selector - Only for Mega Admins */}
          <TenantSelector />

          {/* Project Selector */}
          <ProjectSelector />

          {/* API Documentation Button */}
          <Link
            to="/api-docs"
            className="flex items-center space-x-2 px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <Book className="h-4 w-4" />
            <span className="text-sm hidden sm:inline">{t('api_docs')}</span>
          </Link>

          {/* Notification Bell */}
          <NotificationBell userId={user?.authUserId} onJobCompleted={handleJobCompleted} />

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-100 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label={t('header.toggle_theme')}
            title={t('header.toggle_theme')}
          >
            {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
          </button>

          {/* Language Selector */}
          <div className="relative language-selector">
            <button
              onClick={() => setShowLanguageMenu(!showLanguageMenu)}
              className="flex items-center space-x-2 p-2 text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-100 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <Globe className="h-5 w-5" />
              <span className="text-sm font-medium hidden sm:inline">{currentLanguage.flag}</span>
            </button>

            {/* Language Menu */}
            {showLanguageMenu && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-50 language-menu">
                <div className="p-2">
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-100 dark:border-gray-700 mb-2">
                    {t('language')}
                  </div>
                  {languages.map((language) => (
                    <button
                      key={language.code}
                      onClick={() => changeLanguage(language.code)}
                      className={`w-full flex items-center space-x-3 px-3 py-2 text-sm rounded-lg transition-colors ${
                        i18n.language === language.code
                          ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 font-medium'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <span className="text-lg">{language.flag}</span>
                      <span>{language.name}</span>
                      {i18n.language === language.code && (
                        <div className="ml-auto w-2 h-2 bg-blue-500 dark:bg-blue-400 rounded-full"></div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={logout}
            className="flex items-center space-x-2 px-2 sm:px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            <span className="text-sm hidden sm:inline">{t('logout')}</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;